import { ChangeDetectionStrategy, Component, computed, effect, signal, WritableSignal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DateRangeElement } from '@iot-platform/iot-platform-utils';
import { I4BGridFilterOptions } from '@iot-platform/models/grid-engine';
import { Subject } from 'rxjs';
import { DateRangeComponent } from '../../../../date-range/date-range.component';
import { FormControlType, FormField, FormGroupComponent } from '../../../../forms';
import { AbstractFilterEngineFormComponent } from '../abstract-filter-form.component';

export enum DateFormControlNames {
  DATE = 'date'
}

export interface DateValue {
  date?: DateRangeElement | null;
  startDate?: DateRangeElement | null;
  endDate?: DateRangeElement | null;
}

@Component({
  selector: 'iot-platform-ui-filter-date-field',
  standalone: true,
  imports: [FormGroupComponent, DateRangeComponent],
  templateUrl: './filter-date-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterDateFieldComponent extends AbstractFilterEngineFormComponent<DateValue> {
  readonly showDateRange = computed(() => {
    const operator = this.selectedOperator();
    return operator?.value === I4BGridFilterOptions.IN_RANGE;
  });
  dateFormatterPattern = computed(() => {
    const options = this.fieldOptions();
    return options?.dateFormatterPattern;
  });

  clearDateRangeFilters$ = new Subject<void>();

  startDate: WritableSignal<DateRangeElement | null> = signal(null);
  endDate: WritableSignal<DateRangeElement | null> = signal(null);
  date: WritableSignal<DateRangeElement | null> = signal(null);

  constructor() {
    super();

    this.form.addControl(DateFormControlNames.DATE, new FormControl<Date | null>(null));

    this.initializeDisabledState();
    this.onShowDateRange();
  }

  get dateControl(): FormControl<Date | null> {
    return this.form.get(DateFormControlNames.DATE) as FormControl<Date | null>;
  }

  onDateRangeChange(event: { startDate: DateRangeElement; endDate: DateRangeElement }): void {
    this.startDate.set(event.startDate);
    this.endDate.set(event.endDate);
  }

  // Not used for date picker field
  getValueDisplay(): string {
    return '';
  }

  getControlValue(): DateValue {
    if (this.showDateRange()) {
      const startDate = this.startDate();
      const endDate = this.endDate();
      return { startDate, endDate };
    } else {
      const date = this.date();
      return { date };
    }
  }

  hasValidValue(): boolean {
    const value: DateValue = this.getControlValue();
    if (this.showDateRange()) {
      const startDate = value?.startDate?.date;
      const endDate = value?.endDate?.date;
      const emptyStartDate = startDate === null || startDate === undefined;
      const emptyEndDate = endDate === null || endDate === undefined;
      if (!emptyStartDate && !emptyEndDate) {
        return !emptyStartDate && !emptyEndDate && !Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime()) && startDate.getTime() <= endDate.getTime();
      }
      if (!emptyStartDate) {
        return !Number.isNaN(startDate.getTime());
      }
      if (!emptyEndDate) {
        return !Number.isNaN(endDate.getTime());
      }
      return false;
    } else {
      const date = value?.date?.date;
      return date !== null && date !== undefined && !Number.isNaN(date.getTime());
    }
  }

  getSearchFields(): Partial<FormField>[] {
    return [
      {
        ...this.searchTermField,
        type: FormControlType.DATE_TIME_PICKER,
        name: signal(DateFormControlNames.DATE),
        placeholderHours: signal('FILTER_ENGINE.DATE_PLACEHOLDER_HOURS'),
        placeholderMinutes: signal('FILTER_ENGINE.DATE_PLACEHOLDER_MINUTES'),
        clearLabel: signal('FILTER_ENGINE.CLEAR'),
        disabled: this.disabled,
        onClear: () => this.clearValue(),
        selectionChange: (event) => this.date.set(event?.value)
      }
    ];
  }

  override clearValue(): void {
    this.dateControl?.reset(null, { emitEvent: false });
    this.clearDateRangeFilters$.next();
    this.date.set(null);
    this.startDate.set(null);
    this.endDate.set(null);
  }

  override emitFilter(value: any): void {
    const field = this.field();
    const operator = this.selectedOperator();

    if (!operator) {
      console.warn('[FilterDateField] No operator selected for field:', field.key);
      return;
    }

    if (this.showDateRange()) {
      this.emitRangeFilter(value?.startDate, value?.endDate);
    } else {
      super.emitFilter(this.formatValue(value?.date), this.formatLabel(value?.date));
    }
  }

  private onShowDateRange(): void {
    effect(() => {
      const showDateRange = this.showDateRange();
      if (showDateRange) {
        this.form.removeControl(DateFormControlNames.DATE);
      } else if (!this.dateControl) {
        this.form.addControl(DateFormControlNames.DATE, new FormControl<Date | null>(null));
      }
    });
  }

  private emitRangeFilter(startDate: DateRangeElement, endDate: DateRangeElement): void {
    const field = this.field();
    const operator = this.selectedOperator();

    const filter: any = {
      criteriaKey: field.options.criteriaKey,
      criteriaLabel: field.fullLabel,
      criteriaOperator: operator?.value,
      value: {
        startDate: {
          value: this.formatValue(startDate),
          valueDisplay: this.formatLabel(startDate)
        },
        endDate: {
          value: this.formatValue(endDate),
          valueDisplay: this.formatLabel(endDate)
        }
      },
      isDatePickerBatch: true,
      fieldKey: field.key
    };

    this.filterApplied.emit({ filter, field });
  }

  private formatLabel(date: DateRangeElement): string {
    return date?.label;
  }

  private formatValue(date: DateRangeElement): string {
    return date?.value;
  }

  private initializeDisabledState(): void {
    effect(() => {
      const isDisabled = this.disabled();
      const action = isDisabled ? 'disable' : 'enable';
      this.dateControl[action]({ emitEvent: false });
    });
  }
}
